/**
 * Attaching from Cloud: the popup, and what Relay accepts back from it.
 *
 * Relay's web page never holds a vault key, so it cannot upload or mint a link
 * itself. It opens Cloud's `/picker?for=relay` in a popup, Cloud does both with
 * the vault it has open, and posts back one `WireFile` per file — the fields
 * the Qt client puts in `msg.f`. This page takes a message as that result only
 * when it comes from the popup this page opened, on Cloud's origin, in exactly
 * that shape. Anything else is dropped without a word: a page that answers a
 * malformed message is a page that tells its sender what it is listening for.
 *
 * Not one of the four files the CLI vendors from this directory.
 */

import type { WireFile } from './payload';
import type { Attachment, Message, SendOptions } from './types';

export const PICKER_MESSAGE = 'mauvely-cloud-picker';

/** Files one result may carry. Cloud's picker stops at the same number. */
export const MAX_PICKED_FILES = 20;

/**
 * `SendOptions` with the files a picker handed back. Here rather than in
 * `types.ts`, which the CLI vendors and has no use for this.
 */
export interface SendWithFiles extends SendOptions {
  files?: WireFile[];
}

/** The Cloud beside this Relay: production, staging, or the local stack. */
export function cloudOrigin(
  hostname: string = typeof location === 'undefined' ? '' : location.hostname
): string {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return `http://${hostname}:8788`;
  if (hostname === 'relay.mauvely.dev') return 'https://cloud.mauvely.dev';
  return 'https://cloud.mauvely.com';
}

/**
 * Open the picker. Call it straight from the click: a popup opened after an
 * `await` is a popup the browser blocks. Null when it was blocked anyway.
 */
export function openCloudPicker(): Window | null {
  const width = 520;
  const height = 680;
  const left = Math.round(window.screenX + (window.outerWidth - width) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - height) / 2);
  return window.open(
    `${cloudOrigin()}/picker?for=relay`,
    '_blank',
    `popup,width=${width},height=${height},left=${left},top=${top}`
  );
}

// Cloud's ids are `prefix_` and hex; the secret is 32 bytes as base64url.
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const SECRET = /^[A-Za-z0-9_-]{43}$/;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isText = (v: unknown, max: number, min = 0): v is string =>
  typeof v === 'string' && v.length >= min && v.length <= max;

function fileOf(raw: unknown): WireFile | null {
  if (!isObject(raw)) return null;
  const { id, s, l, r, x, m, b } = raw;
  if (typeof id !== 'string' || !ID.test(id)) return null;
  if (typeof s !== 'string' || !SECRET.test(s)) return null;
  if (typeof l !== 'string' || !ID.test(l)) return null;
  if (typeof r !== 'string' || !ID.test(r)) return null;
  if (!isText(x, 255, 1) || !isText(m, 255)) return null;
  if (typeof b !== 'number' || !Number.isSafeInteger(b) || b < 0) return null;
  // A fresh object, so nothing else the sender put beside these travels on.
  return { id, s, l, r, x, m, b };
}

/**
 * The files in a picker result, or null for anything that is not one from
 * `expected.source` on `expected.origin`.
 */
export function readPickerMessage(
  event: { origin: string; source: unknown; data: unknown },
  expected: { origin: string; source: unknown }
): WireFile[] | null {
  if (event.origin !== expected.origin) return null;
  if (!expected.source || event.source !== expected.source) return null;

  const data = event.data;
  if (!isObject(data) || data['type'] !== PICKER_MESSAGE || data['v'] !== 1) return null;
  const files = data['files'];
  if (!Array.isArray(files) || files.length < 1 || files.length > MAX_PICKED_FILES) return null;

  const out: WireFile[] = [];
  for (const raw of files) {
    const file = fileOf(raw);
    if (!file) return null;
    out.push(file);
  }
  return out;
}

/** A file this device is sending, drawn as `decodeEntry` draws a received one. */
export function attachmentOf(file: WireFile, origin: string = cloudOrigin()): Attachment {
  return {
    nodeId: file.id,
    name: file.x ?? '',
    mime: file.m ?? '',
    bytes: file.b ?? 0,
    cloudUrl: file.l && file.s ? `${origin}/s/${file.l}#${file.s}` : ''
  };
}

/** Every file in `messages`, newest message first, with the message it came in. */
export function attachmentsIn(messages: Message[]): { file: Attachment; message: Message }[] {
  return [...messages]
    .sort((a, b) => b.at - a.at)
    .flatMap((message) => message.attachments.map((file) => ({ file, message })));
}
